import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Menu, X, ShoppingBag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import { useSettings } from "@/context/SettingsContext";
import { STORE } from "@/lib/config";

const NAV_LINKS = [
  { href: "#pocetna", label: "Početna" },
  { href: "#o-nama", label: "O nama" },
  { href: "#popravak", label: "Servis" },
  { href: "#fiskalizacija", label: "Kase" },
  { href: "#knjizara", label: "Knjižara" },
  { href: "#proizvodi", label: "Proizvodi" },
  { href: "#kontakt", label: "Kontakt" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { count, setOpen: setCartOpen } = useCart();
  const { resolved } = useSettings();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <nav
      data-testid="main-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/80 backdrop-blur-xl border-b border-neutral-200/60 py-3"
          : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
        {/* LOGO */}
        <a href="#pocetna" data-testid="nav-logo" className="flex items-center gap-3">
          {/* [USER] LOGO: postavi preko Admin panela ili IMAGES.logo u config.js */}
          {resolved.logo ? (
            <img src={resolved.logo} alt="Moby Shop logo" className="h-10 w-10 object-contain" />
          ) : (
            <div className="h-10 w-10 rounded-xl bg-[#D32F2F] grid place-items-center text-white font-bold shadow-lg shadow-red-200">
              M
            </div>
          )}
          <div className="leading-tight">
            <div
              className={`font-bold text-lg tracking-tight ${
                scrolled ? "text-neutral-900" : "text-white drop-shadow"
              }`}
              style={{ fontFamily: "Cabinet Grotesk" }}
            >
              {STORE.name}
            </div>
            <div
              className={`text-[10px] uppercase tracking-[0.18em] ${
                scrolled ? "text-[#D32F2F]" : "text-white/90"
              }`}
            >
              {STORE.city}
            </div>
          </div>
        </a>

        {/* DESKTOP LINKS */}
        <div className="hidden md:flex items-center gap-1">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                scrolled
                  ? "text-neutral-700 hover:text-[#D32F2F] hover:bg-red-50"
                  : "text-white/90 hover:text-white hover:bg-white/10"
              }`}
            >
              {l.label}
            </a>
          ))}
          <Link
            to="/admin"
            data-testid="nav-admin-link"
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              scrolled ? "text-neutral-500 hover:text-[#D32F2F]" : "text-white/70 hover:text-white"
            }`}
          >
            Admin
          </Link>
          <Button
            data-testid="nav-cart-btn"
            onClick={() => setCartOpen(true)}
            className="ml-2 rounded-full bg-[#D32F2F] hover:bg-[#B71C1C] text-white px-5"
          >
            <ShoppingBag className="w-4 h-4 mr-2" />
            Košarica
            {count > 0 && (
              <span className="ml-2 bg-white text-[#D32F2F] text-xs font-bold rounded-full w-5 h-5 grid place-items-center">
                {count}
              </span>
            )}
          </Button>
        </div>

        {/* MOBILE TOGGLE */}
        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen(!open)}
          className={`md:hidden p-2 rounded-lg ${scrolled ? "text-neutral-900" : "text-white"}`}
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {/* MOBILE MENU */}
      {open && (
        <div className="md:hidden mt-3 mx-4 rounded-2xl bg-white border border-neutral-200 p-4 space-y-1 shadow-xl">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={() => setOpen(false)}
              className="block px-4 py-3 rounded-lg text-neutral-700 hover:bg-red-50 hover:text-[#D32F2F]"
            >
              {l.label}
            </a>
          ))}
          <Link
            to="/admin"
            onClick={() => setOpen(false)}
            className="block px-4 py-3 rounded-lg text-neutral-500"
          >
            Admin
          </Link>
          <button
            onClick={() => {
              setOpen(false);
              setCartOpen(true);
            }}
            className="w-full mt-2 bg-[#D32F2F] text-white rounded-full py-3 font-semibold"
          >
            Košarica ({count})
          </button>
        </div>
      )}
    </nav>
  );
}
